import { Box, CircularProgress, Typography } from "@material-ui/core";
import React, { useEffect } from "react";
import { setActiveStep, resetValidation } from "../../context/bulkUploadActions";
import { useBulkUpload } from "../../hooks/useBulkUpload";
import { useValidation } from "../../hooks/useValidation";
import ValidationTable from "../shared/ValidationTable";

export const ValidationStep = ({ programStartDate, programId }) => {
  const { state, dispatch } = useBulkUpload();
  const { validateFile, validating } = useValidation();

  useEffect(() => {
    if (state.file && state.validation.status === "idle") {
      validateFile(state.file.data, programStartDate, programId);
    }
  }, [state.file, state.validation.status]);

  const handleBack = () => {
    dispatch(resetValidation()); 
    dispatch(setActiveStep(0));
  };

  const handleNext = () => {
    dispatch(setActiveStep(2));
  };

  const handleReupload = () => {
    dispatch(setActiveStep(0));
    dispatch(resetValidation()); 
  };

  if (validating || state.validation.status === "validating") {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" py={8}>
        <CircularProgress size={60} />
        <Typography variant="h6" className="mt-4">
          Validating your file...
        </Typography>
        <Typography variant="body2" color="textSecondary" className="mt-2">
          This may take a moment
        </Typography>
      </Box>
    );
  }

  if (state.validation.status === "error") {
    return (
      <Box py={4}>
        <div className="alert alert-danger">
          <div className="alert-text">
            <strong>Validation Error:</strong>
            <p className="mb-0 mt-2">{state.error}</p>
          </div>
        </div>
        <div className="d-flex justify-content-between mt-5">
          <button className="btn btn-secondary" onClick={handleBack}>
            Back
          </button>
          <button className="btn btn-primary" onClick={handleReupload}>
            Upload New File
          </button>
        </div>
      </Box>
    );
  }

  const { results, summary } = state.validation;
  const hasErrors = results.errors.length > 0;

  return (
    <div className="validation-step">
      {/* Summary Cards */}
      <div className="row mb-6">
        <div className="col-md-3">
          <div className="card card-custom bg-light-success">
            <div className="card-body p-4 text-center">
              <div className="font-weight-boldest font-size-h1 text-success">
                {summary.validRows}
              </div>
              <div className="text-muted font-weight-bold mt-2">
                Valid Records
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card card-custom bg-light-danger">
            <div className="card-body p-4 text-center">
              <div className="font-weight-boldest font-size-h1 text-danger">
                {summary.errorRows}
              </div>
              <div className="text-muted font-weight-bold mt-2">Errors</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card card-custom bg-light-warning">
            <div className="card-body p-4 text-center">
              <div className="font-weight-boldest font-size-h1 text-warning">
                {summary.warningRows}
              </div>
              <div className="text-muted font-weight-bold mt-2">Warnings</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card card-custom bg-light-info">
            <div className="card-body p-4 text-center">
              <div className="font-weight-boldest font-size-h1 text-info">
                {summary.totalRows}
              </div>
              <div className="text-muted font-weight-bold mt-2">Total Rows</div>
            </div>
          </div>
        </div>
      </div>

      {/* Validation Results */}
      <ValidationTable
        errors={results.errors}
        warnings={results.warnings}
        valid={results.valid}
      />

      {/* Actions */}
      <div className="d-flex justify-content-between mt-6">
        <button className="btn btn-secondary" onClick={handleBack}>
          Back
        </button>
        {hasErrors ? (
          <button className="btn btn-primary" onClick={handleReupload}>
            Fix Errors & Re-upload
          </button>
        ) : (
          <button className="btn btn-success" onClick={handleNext}>
            Continue to Review
          </button>
        )}
      </div>
    </div>
  );
};
